import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { PlayerSelector } from '@/components/PlayerSelector'
import { BronzeMatchToggle } from '@/components/BronzeMatchToggle'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Icon, ICONS } from '@/lib/icons'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { KnockoutBracket, KnockoutSeedingStrategy, Player, Sport } from '@/types/api'
import { apiClient, handleApiError } from '@/services/api'
import { resolvePlayerStatsBySport } from '@/lib/player-stats'

type SeedingOption = {
  value: KnockoutSeedingStrategy
  label: string
  description: string
}

const BRACKET_SIZES = [4, 8, 16, 32]
const MIN_PLAYERS_FOR_BRACKET = 4

interface BracketSetupProps {
  seriesId: string
  seriesTitle: string
  seriesSport: Sport
  onCreated: (bracket: KnockoutBracket) => void
  clubId?: string
}

export function BracketSetup({ seriesId, seriesTitle, seriesSport, onCreated, clubId }: BracketSetupProps) {
  const { t } = useTranslation()

  const [selectedPlayers, setSelectedPlayers] = useState<Player[]>([])
  const [bracketSize, setBracketSize] = useState<number>(BRACKET_SIZES[1])
  const [loading, setLoading] = useState(false)
  const [seedingStrategy, setSeedingStrategy] = useState<KnockoutSeedingStrategy>(
    'KNOCKOUT_SEEDING_STRATEGY_MANUAL'
  )
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [lastSeedingHash, setLastSeedingHash] = useState<string>('')
  const [hybridTopCount, setHybridTopCount] = useState<number>(4)
  const [includeBronzeMatch, setIncludeBronzeMatch] = useState<boolean>(true)

  // Helper to get sport-specific stats for a player
  const getPlayerStats = useCallback((player: Player) => {
    const stats = resolvePlayerStatsBySport(player, seriesSport)

    return {
      winRate: stats?.winRate ?? 0,
      totalMatchesPlayed: stats?.totalMatchesPlayed ?? 0
    }
  }, [seriesSport])

  const playersCount = selectedPlayers.length
  const remainingSlots = Math.max(bracketSize - playersCount, 0)
  const orderedPlayers = useMemo(() => selectedPlayers, [selectedPlayers])

  const selectedPlayerIds = useMemo(
    () => selectedPlayers.map(p => p.id),
    [selectedPlayers]
  )

  const seedingOptions = useMemo<SeedingOption[]>(
    () => [
      {
        value: 'KNOCKOUT_SEEDING_STRATEGY_MANUAL',
        label: t('knockout.setup.strategy.manual', 'Manual seeding'),
        description: t(
          'knockout.setup.strategy.manualDescription',
          'Arrange players yourself to control every matchup.'
        )
      },
      {
        value: 'KNOCKOUT_SEEDING_STRATEGY_RANDOM',
        label: t('knockout.setup.strategy.random', 'Random seeding'),
        description: t(
          'knockout.setup.strategy.randomDescription',
          'Shuffle players randomly for a surprise-filled bracket.'
        )
      },
      {
        value: 'KNOCKOUT_SEEDING_STRATEGY_WIN_RATE',
        label: t('knockout.setup.strategy.winRate', 'Win rate seeding'),
        description: t(
          'knockout.setup.strategy.winRateDescription',
          'Seed players by global win rate so favourites meet later.'
        )
      },
      {
        value: 'KNOCKOUT_SEEDING_STRATEGY_HYBRID',
        label: t('knockout.setup.strategy.hybrid', 'Hybrid seeding'),
        description: t(
          'knockout.setup.strategy.hybridDescription',
          'Lock top seeds by win rate and randomize the rest.'
        )
      }
    ],
    [t]
  )

  const seedingOptionMap = useMemo(() => {
    return seedingOptions.reduce<Partial<Record<KnockoutSeedingStrategy, SeedingOption>>>(
      (acc, option) => {
        acc[option.value] = option
        return acc
      },
      {}
    )
  }, [seedingOptions])

  const allowManualAdjustments = seedingStrategy === 'KNOCKOUT_SEEDING_STRATEGY_MANUAL'
  const strategyRequiresPreview = !allowManualAdjustments
  const sortedPlayersKey = useMemo(
    () => selectedPlayers.map((player) => player.id).sort().join('|'),
    [selectedPlayers]
  )
  const seedingHash = computeSeedingHash(
    seedingStrategy,
    sortedPlayersKey,
    hybridTopCount,
    bracketSize
  )
  const activeStrategy = seedingOptionMap[seedingStrategy]
  const canPreview =
    strategyRequiresPreview && playersCount >= MIN_PLAYERS_FOR_BRACKET && bracketSize > 0

  const previewSeeding = useCallback(
    async (players: Player[]) => {
      if (!strategyRequiresPreview) {
        return
      }
      if (players.length < MIN_PLAYERS_FOR_BRACKET) {
        setPreviewError(null)
        return
      }

      setPreviewLoading(true)
      try {
        const response = await apiClient.previewKnockoutSeeding({
          seriesId,
          playerIds: players.map((player) => player.id),
          strategy: seedingStrategy,
          hybridSeedCount:
            seedingStrategy === 'KNOCKOUT_SEEDING_STRATEGY_HYBRID' ? hybridTopCount : undefined,
          bracketSize
        })

        setSelectedPlayers((prev) => {
          const map = new Map(prev.map((player) => [player.id, player]))
          const seen = new Set<string>()
          const ordered: Player[] = []

          response.playerIds.forEach((id) => {
            const player = map.get(id)
            if (player) {
              ordered.push(player)
              seen.add(id)
            }
          })

          prev.forEach((player) => {
            if (!seen.has(player.id)) {
              ordered.push(player)
            }
          })

          return ordered
        })

        setPreviewError(null)
      } catch (error) {
        handleApiError(error, (apiError) => {
          setPreviewError(apiError.message || t('errors.generic'))
        })
      } finally {
        setPreviewLoading(false)
      }
    },
    [bracketSize, hybridTopCount, seedingStrategy, seriesId, strategyRequiresPreview, t]
  )

  useEffect(() => {
    setHybridTopCount((prev) => {
      if (bracketSize <= 0) {
        return prev
      }
      const clamped = Math.min(Math.max(prev, 1), bracketSize)
      return clamped
    })
  }, [bracketSize])

  useEffect(() => {
    if (!strategyRequiresPreview) {
      setPreviewError(null)
      setPreviewLoading(false)
      setLastSeedingHash('')
      return
    }

    if (!canPreview) {
      return
    }

    if (seedingHash === lastSeedingHash) {
      return
    }

    setLastSeedingHash(seedingHash)
    previewSeeding(selectedPlayers).catch(() => undefined)
  }, [
    strategyRequiresPreview,
    canPreview,
    seedingHash,
    lastSeedingHash,
    previewSeeding,
    selectedPlayers
  ])

  const handlePlayerSelected = (player: Player) => {
    if (selectedPlayers.some((p) => p.id === player.id)) {
      toast.warning(t('knockout.setup.duplicatePlayer', 'Player already added to bracket'))
      return
    }

    if (playersCount >= bracketSize) {
      toast.warning(t('knockout.setup.bracketFull', 'Bracket already has the maximum number of players'))
      return
    }

    setSelectedPlayers((prev) => [...prev, player])
    setLastSeedingHash('')
    setPreviewError(null)
  }

  const handleRemovePlayer = (index: number) => {
    setSelectedPlayers((prev) => prev.filter((_, i) => i !== index))
    setLastSeedingHash('')
    setPreviewError(null)
  }

  const handleMovePlayer = (index: number, direction: 'up' | 'down') => {
    if (!allowManualAdjustments) {
      return
    }
    setSelectedPlayers((prev) => {
      const next = [...prev]
      const targetIndex = direction === 'up' ? index - 1 : index + 1
      if (targetIndex < 0 || targetIndex >= prev.length) {
        return prev
      }
      const temp = next[targetIndex]
      next[targetIndex] = next[index]
      next[index] = temp
      return next
    })
  }

  const handleCreateBracket = async () => {
    if (playersCount < MIN_PLAYERS_FOR_BRACKET || playersCount > bracketSize) {
      toast.error(
        t(
          'knockout.setup.validation.playerCount',
          'Select between {{min}} and {{size}} players before creating the bracket',
          { min: MIN_PLAYERS_FOR_BRACKET, size: bracketSize }
        )
      )
      return
    }

    try {
      setLoading(true)
      const response = await apiClient.createKnockoutBracket(seriesId, {
        playerIds: orderedPlayers.map((player) => player.id),
        bracketSize,
        seedingStrategy,
        hybridSeedCount:
          seedingStrategy === 'KNOCKOUT_SEEDING_STRATEGY_HYBRID' ? hybridTopCount : undefined,
        bronzeMedalMatch: includeBronzeMatch
      })
      toast.success(t('knockout.setup.created', 'Bracket created successfully'))
      onCreated(response.bracket)
      setSelectedPlayers([])
      setLastSeedingHash('')
      setPreviewError(null)
    } catch (error) {
      handleApiError(error, (apiError) => {
        toast.error(apiError.message || t('errors.generic'))
      })
    } finally {
      setLoading(false)
    }
  }

  const handleBracketSizeChange = (size: number) => {
    setBracketSize(size)
    if (selectedPlayers.length > size) {
      setSelectedPlayers((prev) => prev.slice(0, size))
    }
    setLastSeedingHash('')
    setPreviewError(null)
  }

  const handleStrategyChange = (value: string) => {
    const strategy = value as KnockoutSeedingStrategy
    setSeedingStrategy(strategy)
    setLastSeedingHash('')
    setPreviewError(null)
  }

  const handleHybridTopCountChange = (value: string) => {
    const parsed = Number.parseInt(value, 10)
    const clamped = Number.isNaN(parsed) ? 1 : Math.min(Math.max(parsed, 1), bracketSize)
    setHybridTopCount(clamped)
    setLastSeedingHash('')
  }

  const handleGenerateSeeding = () => {
    if (!canPreview) {
      return
    }
    previewSeeding(selectedPlayers).catch(() => undefined)
  }

  const handleClearSelection = () => {
    setSelectedPlayers([])
    setLastSeedingHash('')
    setPreviewError(null)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('knockout.setup.title', 'Create knockout bracket')}</CardTitle>
        <CardDescription>
          {t('knockout.setup.description', {
            title: seriesTitle,
            count: playersCount,
            size: bracketSize
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 md:grid-cols-[220px_1fr]">
          <div className="space-y-5">
            <div className="space-y-3">
              <Label className="text-sm font-medium text-muted-foreground">
                {t('knockout.setup.bracketSize', 'Bracket size')}
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {BRACKET_SIZES.map((size) => (
                  <Button
                    key={size}
                    type="button"
                    variant={size === bracketSize ? 'default' : 'outline'}
                    onClick={() => handleBracketSizeChange(size)}
                    disabled={loading}
                  >
                    {size}
                  </Button>
                ))}
              </div>
              <div className="text-xs text-muted-foreground space-y-1">
                <div>{t('knockout.setup.remaining', { remaining: remainingSlots })}</div>
                {remainingSlots > 0 && (
                  <div>
                    {t('knockout.setup.byeNotice', {
                      count: remainingSlots
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-medium text-muted-foreground">
                {t('knockout.setup.seedingStrategyLabel', 'Seeding strategy')}
              </Label>
              <Select
                value={seedingStrategy}
                onValueChange={handleStrategyChange}
                disabled={loading || previewLoading}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t(
                      'knockout.setup.seedingStrategyPlaceholder',
                      'Choose how players should be seeded'
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {seedingOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {activeStrategy && (
                <p className="text-xs text-muted-foreground">{activeStrategy.description}</p>
              )}

              {seedingStrategy === 'KNOCKOUT_SEEDING_STRATEGY_HYBRID' && (
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-muted-foreground">
                    {t('knockout.setup.hybridTopSeeds', 'Number of top seeds')}
                  </Label>
                  <Input
                    type="number"
                    min={1}
                    max={bracketSize}
                    value={hybridTopCount}
                    onChange={(event) => handleHybridTopCountChange(event.target.value)}
                    disabled={loading}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t(
                      'knockout.setup.hybridTopSeedsDescription',
                      'Lock the strongest players into seeded positions before randomising the rest.'
                    )}
                  </p>
                </div>
              )}

              {strategyRequiresPreview && (
                <div className="space-y-2">
                  {canPreview ? (
                    <div className="space-y-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={handleGenerateSeeding}
                        disabled={previewLoading || loading}
                      >
                        {previewLoading
                          ? t('knockout.setup.seedingLoading', 'Generating seeding…')
                          : t('knockout.setup.generateSeeding', 'Generate seeding')}
                      </Button>
                      {previewError ? (
                        <p className="text-xs text-destructive">{previewError}</p>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          {previewLoading
                            ? t('knockout.setup.seedingLoadingStatus', 'Calculating player order…')
                            : t('knockout.setup.seedingAppliedStatus', {
                                strategy:
                                  activeStrategy?.label ??
                                  t('knockout.setup.strategy.manual', 'Manual seeding')
                              })}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {t('knockout.setup.seedingRequiresPlayers', { size: bracketSize })}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium text-muted-foreground">
              {t('knockout.setup.addPlayer', 'Add players in seeding order')}
            </Label>
            <PlayerSelector
              onPlayerSelected={handlePlayerSelected}
              clubId={clubId}
              excludePlayerIds={selectedPlayerIds}
              className="w-full"
            />
            <div className="text-xs text-muted-foreground">
              {playersCount > 0
                ? t('knockout.setup.playerCount', { count: playersCount, size: bracketSize })
                : t('knockout.setup.empty', 'Select players to build your bracket')}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <Label className="text-sm font-medium text-muted-foreground">
            {t('knockout.setup.seedOrder', 'Seed order')}
          </Label>
          <div className="space-y-2">
            {orderedPlayers.length === 0 && (
              <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                {t('knockout.setup.empty', 'Select players to build your bracket')}
              </div>
            )}

            {orderedPlayers.map((player, index) => (
              <div
                key={player.id}
                className="flex items-center justify-between rounded-md border bg-card px-3 py-2 text-sm shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <BadgeNumber value={index + 1} />
                  <div>
                    <div className="font-medium">{player.displayName}</div>
                    <div className="text-xs text-muted-foreground">
                      {(() => {
                        const stats = getPlayerStats(player)
                        return t('knockout.setup.playerWinRate', {
                          winRate: formatWinRate(stats.winRate),
                          matches: stats.totalMatchesPlayed
                        })
                      })()}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => handleMovePlayer(index, 'up')}
                    disabled={index === 0 || loading || !allowManualAdjustments}
                    aria-label={t('knockout.setup.moveUp', 'Move up')}
                  >
                    <Icon icon={ICONS.arrowUp} width={16} height={16} />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => handleMovePlayer(index, 'down')}
                    disabled={
                      index === orderedPlayers.length - 1 || loading || !allowManualAdjustments
                    }
                    aria-label={t('knockout.setup.moveDown', 'Move down')}
                  >
                    <Icon icon={ICONS.arrowDown} width={16} height={16} />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => handleRemovePlayer(index)}
                    disabled={loading}
                    aria-label={t('knockout.setup.removePlayer', 'Remove player')}
                  >
                    <Icon icon={ICONS.close} width={16} height={16} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bronze Match Toggle */}
        <BronzeMatchToggle
          id="bronze-switch"
          checked={includeBronzeMatch}
          onCheckedChange={setIncludeBronzeMatch}
          className="rounded-md border bg-muted/40 p-4"
          label={t('series.bronzeMatch.label', 'Play bronze medal match')}
          description={t(
            'series.bronzeMatch.description',
            'If enabled, semifinal losers play for third place.'
          )}
        />

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClearSelection}
            disabled={loading || orderedPlayers.length === 0}
          >
            {t('knockout.setup.clear', 'Clear selection')}
          </Button>
          <Button
            type="button"
            onClick={handleCreateBracket}
            disabled={loading || playersCount < MIN_PLAYERS_FOR_BRACKET || playersCount > bracketSize}
          >
            {loading ? t('common.loading') : t('knockout.setup.create', 'Create bracket')}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function BadgeNumber({ value }: { value: number }) {
  return (
    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
      {value}
    </div>
  )
}

function formatWinRate(winRate?: number): string {
  if (typeof winRate !== 'number' || Number.isNaN(winRate)) {
    return '0%'
  }
  const percentage = Math.max(0, winRate) * 100
  return `${percentage.toFixed(1)}%`
}

function computeSeedingHash(
  strategy: KnockoutSeedingStrategy,
  sortedKey: string,
  hybridTopCount: number,
  bracketSize: number
): string {
  const hybridPart = strategy === 'KNOCKOUT_SEEDING_STRATEGY_HYBRID' ? hybridTopCount : 0
  return `${strategy}|${sortedKey}|${hybridPart}|${bracketSize}`
}
